import { type RequestOf, type ResponseOf, type RouteName, routes } from './routes.ts';
import { errorResponseSchema } from './schemas.ts';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ClientOptions {
  baseUrl: string;
  getToken: () => Promise<string | null> | string | null;
  /** Extra headers on every request (the browser's device id). */
  headers?: () => Record<string, string>;
  fetch?: typeof fetch;
}

export type ApiClient = { [K in RouteName]: (body: RequestOf<K>) => Promise<ResponseOf<K>> };

/** Typed client: every response is validated with its zod schema before it reaches the UI. */
export function createApiClient(opts: ClientOptions): ApiClient {
  const doFetch = opts.fetch ?? fetch;
  const call = async (name: RouteName, body: unknown) => {
    const route = routes[name];
    const token = await opts.getToken();
    let url = opts.baseUrl.replace(/\/$/, '') + route.path;
    if (route.method === 'GET' && route.request && body) {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(route.request.parse(body) as Record<string, unknown>)) if (v !== undefined) q.set(k, String(v));
      if (q.size > 0) url += `?${q}`;
    }
    const res = await doFetch(url, {
      method: route.method,
      headers: {
        ...opts.headers?.(),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(route.method === 'POST' ? { 'content-type': 'application/json' } : {}),
      },
      body: route.method === 'POST' ? JSON.stringify(route.request ? route.request.parse(body) : body) : undefined,
    });
    const json: unknown = await res.json().catch(() => null);
    if (!res.ok) {
      const err = errorResponseSchema.safeParse(json);
      throw err.success
        ? new ApiError(res.status, err.data.error.code, err.data.error.message)
        : new ApiError(res.status, 'HTTP_' + res.status, res.statusText || 'Request failed');
    }
    return route.response.parse(json);
  };
  return Object.fromEntries(Object.keys(routes).map((k) => [k, (body: unknown) => call(k as RouteName, body)])) as ApiClient;
}
